import type { Notification } from "@/interfaces/notification";
import {
  Shield,
  Bell,
  CheckCircle2,
  AlertTriangle,
  CheckCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import type { AxiosResponse } from "axios";
import type { UseMutationResult } from "@tanstack/react-query";
import { queryClient } from "@/services/queryClient";
import { useNavigate } from "react-router-dom";

interface Props {
  n: Notification;
  markAsReadMutation: UseMutationResult<
    AxiosResponse<any, any, {}>,
    Error,
    string,
    {
      previous: unknown;
    }
  >;
  userId: string;
}

const NotificationItem = ({ n, markAsReadMutation, userId }: Props) => {
  const isRead = n.recipients?.some((r: any) => r.isRead);

  const navigate = useNavigate();

  console.log("NOTIFICATION", n);

  const iconMap: any = {
    SECURITY: <Shield className="w-4 h-4 text-red-500" />,
    TRADE: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
    SYSTEM: <Bell className="w-4 h-4 text-blue-500" />,
    DEFAULT: <AlertTriangle className="w-4 h-4 text-orange-500" />,
  };

  const markAsRead = () => {
    markAsReadMutation.mutate(n.id);
    queryClient.invalidateQueries({ queryKey: ["unread-count", userId] });
  };

  const handleClick = () => {
    if (n.meta?.tradeId) {
      navigate(`${n?.meta?.endpoint}/${n?.meta?.tradeId}`);
    }

    if (n?.meta?.kycId) {
      navigate("/dashboard/kyc");
    }

    markAsRead();
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative cursor-pointer flex gap-4 p-4 rounded-xl transition hover:bg-muted/40 ${
        !isRead ? "bg-muted/20" : ""
      }`}
    >
      <div className="relative flex flex-col items-center">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center border ${
            n.notifType === "SECURITY"
              ? "border-red-200 bg-red-50"
              : n.notifType === "TRADE"
                ? "border-green-200 bg-green-50"
                : "border-blue-200 bg-blue-50"
          }`}
        >
          {iconMap[n.notifType] || iconMap.DEFAULT}
        </div>

        <div className="flex-1 w-px bg-border mt-2" />
      </div>

      <div className="flex-1">
        <div className="flex justify-between items-start gap-3">
          <div>
            <h3 className="font-medium text-sm flex items-center gap-2">
              {n.title}
              {!isRead && <span className="w-2 h-2 rounded-full bg-blue-500" />}
            </h3>

            <p className="text-xs text-muted-foreground mt-1">
              {n.description}
            </p>

            <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground">
              <span>{new Date(n.createdAt).toLocaleString()}</span>
              <span>•</span>
              <span>{n.notifType}</span>
            </div>
          </div>

          {!isRead && (
            <Button size="sm" variant="ghost" onClick={markAsRead}>
              <CheckCheck className="w-4 h-4 mr-1" />
              Read
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
